import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, SearchIcon } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { pcmpProjectPickerQueryOptions } from '../../api/pcmpProjects.api';
import { pcmpReportFilterOptionsQueryOptions } from '../../api/pcmpReports.api';
import type { PcmpReportFilterController } from '../../hooks/usePcmpReportFilters';
import type { PcmpDivision } from '../../types/pcmpQueue.types';
import { PcmpCodeSelect } from '../PcmpQueue/PcmpCodeSelect';

interface PcmpReportFilterBarProps {
  controller: PcmpReportFilterController;
  /** Keeps element ids unique per report, e.g. `pcmp-pending`. */
  idPrefix: string;
  /** The report's own controls, after the shared filters. */
  children?: ReactNode;
  /** What the dates are of: proposals by default. */
  dateSubject?: string;
}

/** The project, division, region and date filters every PCMP report shares. */
export function PcmpReportFilterBar({
  controller,
  idPrefix,
  children,
  dateSubject = 'Proposed',
}: PcmpReportFilterBarProps) {
  const { filters, activeFilterCount, isDateRangeInvalid, setDraft, handleApply, handleReset } =
    controller;

  const { data: projects, isLoading: projectsLoading } = useQuery(pcmpProjectPickerQueryOptions());
  const { data: options, isLoading: optionsLoading } = useQuery(
    pcmpReportFilterOptionsQueryOptions(),
  );

  // A region belongs to one division, so a division narrows the regions.
  const regions = useMemo(
    () =>
      options?.departments
        .filter((department) => !filters.division || department.division === filters.division)
        .map(({ id, name }) => ({ id, name })),
    [options, filters.division],
  );

  const handleDivisionChange = (division: PcmpDivision | undefined) => {
    setDraft('division', division);
    const region = options?.departments.find(
      (department) => department.id === filters.departmentId,
    );
    if (division && region && region.division !== division) setDraft('departmentId', undefined);
  };

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleApply();
      }}
      className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-project`}>Project</Label>
          <OptionSelect
            id={`${idPrefix}-project`}
            options={projects}
            isLoading={projectsLoading}
            value={filters.projectId}
            onChange={(value) => setDraft('projectId', value)}
            placeholder="All"
            className="h-10"
            clearable
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-division`}>Division</Label>
          <PcmpCodeSelect<PcmpDivision>
            id={`${idPrefix}-division`}
            options={options?.divisions}
            value={filters.division}
            onChange={handleDivisionChange}
            disabled={optionsLoading}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-region`}>Region</Label>
          <OptionSelect
            id={`${idPrefix}-region`}
            options={regions}
            isLoading={optionsLoading}
            value={filters.departmentId}
            onChange={(value) => setDraft('departmentId', value)}
            placeholder="All"
            className="h-10"
            clearable
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-from`}>{dateSubject} From</Label>
          <DatePickerInput
            id={`${idPrefix}-from`}
            value={filters.dateFrom}
            onChange={(value) => setDraft('dateFrom', value)}
            aria-invalid={isDateRangeInvalid}
            className="sm:h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-to`}>{dateSubject} To</Label>
          <DatePickerInput
            id={`${idPrefix}-to`}
            value={filters.dateTo}
            onChange={(value) => setDraft('dateTo', value)}
            aria-invalid={isDateRangeInvalid}
            className="sm:h-10"
          />
        </div>
        {children}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-destructive" role="alert">
          {isDateRangeInvalid ? `${dateSubject} To must be on or after ${dateSubject} From.` : null}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="submit" disabled={isDateRangeInvalid} className="h-10 gap-1.5 shadow-sm">
            <SearchIcon className="size-4" />
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={activeFilterCount === 0}
            className="h-10 gap-1.5 shadow-sm"
          >
            <RotateCcw className="size-4" />
            Reset{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        </div>
      </div>
    </form>
  );
}
